import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../lib/auth';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authService.login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    try {
      const response = await api.forgotPassword(forgotEmail);
      toast.success(response.data.message || 'Password reset request submitted');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit password reset request');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1762951566390-e35eb7dfe9c5?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
      >
        <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
          <div className="text-center text-white px-8">
            <img src="/exampur-logo.png" alt="Exampur" className="w-32 h-32 mx-auto mb-6 object-contain" />
            <h1 className="text-5xl font-bold font-headings uppercase tracking-tight mb-4">Exampur</h1>
            <p className="text-xl font-body text-slate-200">Management Software</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {!showForgotPassword ? (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-bold font-headings text-slate-900 mb-2">Welcome Back</h2>
                <p className="text-slate-600 font-body">Sign in to your account to continue</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 font-body mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent font-body"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 font-body mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="login-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent font-body pr-12"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-slate-600 hover:text-slate-900 hover:underline font-body"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white py-3 rounded-md hover:bg-slate-800 transition-all shadow-sm font-medium font-body disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <p className="text-center text-slate-600 font-body mt-6">
                New agent?{' '}
                <a href="/register" className="text-slate-900 font-semibold hover:underline">
                  Register here
                </a>
              </p>
            </>
          ) : (
            <>
              {/* Forgot Password Form */}
              <div className="mb-8">
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
                >
                  <ArrowLeft size={18} />
                  <span className="font-body">Back to login</span>
                </button>
                <h2 className="text-3xl font-bold font-headings text-slate-900 mb-2">Forgot Password</h2>
                <p className="text-slate-600 font-body">
                  Enter your email and we'll send a password reset request to your manager.
                </p>
              </div>

              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700 font-body mb-2">
                    Email Address
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    data-testid="forgot-email-input"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent font-body"
                    placeholder="you@example.com"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="forgot-submit-button"
                  disabled={forgotLoading}
                  className="w-full bg-slate-900 text-white py-3 rounded-md hover:bg-slate-800 transition-all shadow-sm font-medium font-body disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 flex items-center justify-center gap-2"
                >
                  {forgotLoading && <Loader2 size={18} className="animate-spin" />}
                  {forgotLoading ? 'Submitting...' : 'Request Password Reset'}
                </button>
              </form>

              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 font-body">
                  <strong>Note:</strong> Your manager will receive your password reset request and will set a new password for you. 
                  Please contact your manager directly if you need urgent access.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;